import { useState } from 'react'
import {
  Box,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material'
import {
  useGetReferralForVerificationQuery,
  useRespondToReferralVerificationMutation,
  useRespondToReferralUpdateMutation,
} from 'graphql/gen-types'
import { ReferralExpiredView } from 'components/referralVerification/referralExpiredView'
import { ReferralReviewView } from 'components/referralVerification/referralReviewView'
import { ReferralContactUpdateForm } from 'components/referralVerification/referralContactUpdateForm'
import { ReferralContactResourceUpdateForm } from 'components/referralVerification/referralContactResourceUpdateForm'
import { ReferralResourceUpdateForm } from 'components/referralVerification/referralResourceUpdateForm'
import { ResourceInfoFormValues } from 'components/referralVerification/resourceInfoSection'

type SubmitState = 'idle' | 'submitting' | 'success' | 'error'

type UpdateSubmitValues = {
  email?: string
  phone?: string
  firstName?: string
  lastName?: string
  titleRole?: string
  remarks?: string
  resource?: ResourceInfoFormValues
}

export const ReferralVerification = (): JSX.Element => {
  const params = new URLSearchParams(document.location.search.substring(1))
  const id = params.get('id')
  const clientName = params.get('clientName')
  const response = params.get('response')

  const hasRequiredParams = !!id && !!clientName

  const [submitState, setSubmitState] = useState<SubmitState>('idle')

  const {
    data,
    loading: queryLoading,
    error: queryError,
  } = useGetReferralForVerificationQuery({
    variables: {
      getReferralForVerificationArgs: {
        id: id || '',
        clientName: clientName || '',
      },
    },
    skip: !hasRequiredParams,
    fetchPolicy: 'network-only',
  })

  const [respondToReferralVerification] =
    useRespondToReferralVerificationMutation()

  const handleSubmitResponse = async (choice: 'Yes' | 'No'): Promise<void> => {
    if (!id || !clientName) return
    setSubmitState('submitting')
    try {
      const { data: result } = await respondToReferralVerification({
        variables: {
          respondToReferralVerificationArgs: {
            id,
            clientName,
            response: choice,
          },
        },
      })
      if (result?.respondToReferralVerification.success) {
        setSubmitState('success')
      } else {
        setSubmitState('error')
      }
    } catch {
      setSubmitState('error')
    }
  }

  const [respondToReferralUpdate] = useRespondToReferralUpdateMutation()

  const handleUpdateSubmit = async (
    values: UpdateSubmitValues
  ): Promise<void> => {
    if (!id || !clientName) return
    setSubmitState('submitting')
    try {
      const { data: result } = await respondToReferralUpdate({
        variables: {
          respondToReferralUpdateArgs: {
            id,
            clientName,
            contactEmail: values.email,
            contactPhone: values.phone,
            firstName: values.firstName,
            lastName: values.lastName,
            titleRole: values.titleRole,
            remarks: values.remarks,
            resourceAltName: values.resource?.altName,
            resourceStreetAddress: values.resource?.streetAddress,
            resourceCity: values.resource?.city,
            resourceState: values.resource?.state,
            resourceZipCode: values.resource?.zipCode,
            resourceWebsiteAddress: values.resource?.websiteAddress,
            resourcePhoneNumber: values.resource?.phoneNumber,
            resourceFaxNumber: values.resource?.faxNumber,
            resourceEmailAddress: values.resource?.emailAddress,
            resourceApplicationProcedure: values.resource?.applicationProcedure,
            resourceEligibility: values.resource?.eligibility,
            resourceFeeScale: values.resource?.feeScale,
            resourceWebsiteNotes: values.resource?.websiteNotes,
            resourceDescription: values.resource?.description,
          },
        },
      })
      if (result?.respondToReferralUpdate.success) {
        setSubmitState('success')
      } else {
        setSubmitState('error')
      }
    } catch {
      setSubmitState('error')
    }
  }

  const wrapInCard = (children: JSX.Element): JSX.Element => (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        minHeight="100vh"
        py={{ xs: 2, sm: 4 }}
      >
        <Paper sx={{ p: { xs: 2, sm: 4 } }}>{children}</Paper>
      </Box>
    </Container>
  )

  if (!hasRequiredParams) {
    return wrapInCard(
      <ReferralExpiredView message="This referral link is missing required information." />
    )
  }

  if (queryLoading) {
    return wrapInCard(
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (queryError || !data?.getReferralForVerification) {
    return wrapInCard(<ReferralExpiredView />)
  }

  const referral = data.getReferralForVerification

  if (referral.confirmStatus !== 'Unknown') {
    return wrapInCard(<ReferralExpiredView />)
  }

  if (submitState === 'submitting') {
    return wrapInCard(
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (submitState === 'success') {
    return wrapInCard(
      <Typography variant="h6">
        Thank you, your response has been recorded.
      </Typography>
    )
  }

  if (submitState === 'error') {
    return wrapInCard(
      <Typography variant="h6">
        Something went wrong. Please try again.
      </Typography>
    )
  }

  if (response === 'update') {
    const trackingRef = id || ''

    const updateForm =
      referral.requestType === 'CONTACT' ? (
        <ReferralContactUpdateForm
          referral={referral}
          trackingRef={trackingRef}
          onSubmit={handleUpdateSubmit}
        />
      ) : referral.requestType === 'CONTACT_RESOURCE' ? (
        <ReferralContactResourceUpdateForm
          referral={referral}
          trackingRef={trackingRef}
          onSubmit={handleUpdateSubmit}
        />
      ) : (
        <ReferralResourceUpdateForm
          referral={referral}
          trackingRef={trackingRef}
          onSubmit={handleUpdateSubmit}
        />
      )

    return (
      <Container maxWidth="sm">
        <Box py={{ xs: 2, sm: 4 }}>{updateForm}</Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="sm">
      <Box py={{ xs: 2, sm: 4 }}>
        <ReferralReviewView
          referral={referral}
          trackingRef={id || ''}
          initialChoice={
            response === 'yes' ? 'Yes' : response === 'no' ? 'No' : undefined
          }
          onSubmit={handleSubmitResponse}
        />
      </Box>
    </Container>
  )
}
